import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Search, ShieldAlert, Radar, ArrowUpRight } from "lucide-react";
import { fetchCategories, fetchProducts } from "@/lib/api";

const ICON_MAP = {
  search: Search,
  "shield-alert": ShieldAlert,
  radar: Radar,
};

export default function Categories() {
  const [cats, setCats] = useState([]);
  const [counts, setCounts] = useState({});

  useEffect(() => {
    fetchCategories().then(setCats).catch(() => setCats([]));
    fetchProducts().then((all) => {
      const c = {};
      all.forEach((p) => {
        c[p.category_slug] = (c[p.category_slug] || 0) + 1;
      });
      setCounts(c);
    });
  }, []);

  return (
    <section id="categories" data-testid="categories-section" className="relative py-24 md:py-32 bg-slate-50 border-t border-slate-200">
      <div className="max-w-7xl mx-auto px-6 lg:px-12">
        <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-6 mb-14">
          <div>
            <div className="overline mb-3 text-red-600">// 01 · Capability Domains</div>
            <h2 className="font-display font-black uppercase text-[#0A1128] text-4xl md:text-5xl tracking-tighter leading-[0.95] max-w-3xl">
              Three divisions.<br /><span className="text-red-600">One mission.</span>
            </h2>
          </div>
          <p className="text-slate-600 text-sm md:text-base max-w-md leading-relaxed">
            Every product line is built, tested and supplied to operational standards used by Indian defense, paramilitary and law-enforcement agencies.
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-px bg-slate-200 border border-slate-200">
          {cats.map((cat, idx) => {
            const Icon = ICON_MAP[cat.icon] || Search;
            const count = counts[cat.slug] || 0;
            return (
              <Link
                key={cat.slug}
                to={`/products?category=${cat.slug}`}
                data-testid={`category-card-${cat.slug}`}
                className="group relative bg-white p-8 lg:p-10 lift hover:bg-slate-50 hover:border-blue-600 border border-transparent"
              >
                <div className="flex items-start justify-between mb-8">
                  <div className="w-14 h-14 border border-slate-300 flex items-center justify-center bg-white group-hover:border-red-600 group-hover:bg-red-50 transition-colors">
                    <Icon className="w-7 h-7 text-red-600" />
                  </div>
                  <span className="font-mono-tactical text-xs text-slate-400 tracking-widest">0{idx + 1}</span>
                </div>
                <div className="overline mb-2 text-red-600">{cat.tagline}</div>
                <h3 className="font-display font-bold uppercase text-[#0A1128] text-xl tracking-tight mb-3 leading-tight">
                  {cat.name}
                </h3>
                <p className="text-slate-600 text-sm leading-relaxed mb-6">{cat.description}</p>
                <div className="flex items-center justify-between">
                  <div className="inline-flex items-center gap-2 text-blue-600 text-xs font-bold uppercase tracking-widest group-hover:text-blue-700">
                    Browse Equipment <ArrowUpRight className="w-4 h-4 transition-transform group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
                  </div>
                  <span className="font-mono-tactical text-xs text-slate-400 tracking-widest">{count} items</span>
                </div>
              </Link>
            );
          })}
        </div>
      </div>
    </section>
  );
}
